import {useEffect, useRef} from "react";
import appConfig from "../config";

export type ScoreChangedEvent = {
    cmpedt_idt: number;
    match_idt: number;
    at: number;
};

type Options = {
    onScoreChanged?: (evt: ScoreChangedEvent) => void;
    // Optional minimum gap between two callbacks (ms), in case a batch
    // of scores is saved back-to-back.
    debounceMs?: number;
};

// Subscribes to GET /matches/events?cmpEdt=… via EventSource and calls
// onScoreChanged each time the backend pushes a score-changed event for
// the current competition. Reconnects are handled by the browser; on
// unmount / cmpEdt change we close the stream.
export function useMatchEvents(cmpEdt: number | undefined, opts: Options = {}) {
    const handlerRef = useRef(opts.onScoreChanged);
    const debounceRef = useRef(opts.debounceMs ?? 0);
    handlerRef.current = opts.onScoreChanged;
    debounceRef.current = opts.debounceMs ?? 0;

    useEffect(() => {
        if (!cmpEdt) return;
        if (typeof EventSource === 'undefined') return;

        const url = `${appConfig.apiURL}/matches/events?cmpEdt=${cmpEdt}`;
        const es = new EventSource(url);

        let lastFiredAt = 0;
        let pendingTimer: ReturnType<typeof setTimeout> | null = null;

        const fire = (evt: ScoreChangedEvent) => {
            const fn = handlerRef.current;
            if (!fn) return;
            const gap = debounceRef.current;
            if (gap <= 0) { fn(evt); return; }
            const now = Date.now();
            const wait = Math.max(0, lastFiredAt + gap - now);
            if (pendingTimer) clearTimeout(pendingTimer);
            pendingTimer = setTimeout(() => {
                lastFiredAt = Date.now();
                pendingTimer = null;
                fn(evt);
            }, wait);
        };

        es.addEventListener('score-changed', (e: MessageEvent) => {
            try {
                const evt = JSON.parse(e.data) as ScoreChangedEvent;
                fire(evt);
            } catch (err) {
                console.error('useMatchEvents: bad payload', err);
            }
        });

        es.onerror = (err) => {
            // Browser will auto-retry; just log for visibility.
            console.warn('useMatchEvents: stream error', err);
        };

        return () => {
            if (pendingTimer) clearTimeout(pendingTimer);
            es.close();
        };
    }, [cmpEdt]);
}
